import type { GratingTypeWord } from "@/api";
import {
  gratingTypeLabel,
  gratingTypeWidthMm,
  handSetSentence,
  typeChangeSentence,
  type CatalogueSpec,
  type LaidGratings,
} from "@/lib/gratingTypes";

import { Button } from "./Button";
import { releaseLossSentence } from "./ConfirmReleaseLoss";
import { Dialog, DialogFooter } from "./Dialog";
import styles from "./GratingTypeQuestion.module.css";

/**
 * Whether a change of type asks first (R-4, R-7, Ruling G-2): on a released
 * Vorgang, and wherever gratings lie. Otherwise it goes straight through.
 */
export function asksBeforeTypeChange(approved: boolean, laid: LaidGratings | null): boolean {
  return approved || laid !== null;
}

interface GratingTypeQuestionProps {
  /** The type the Vorgang has — `null` on one created before the standard type. */
  from: string | null;
  /** The type it is about to get. */
  to: GratingTypeWord;
  /** The plan answer's catalogue, where the screen has one — the widths the sentence names. */
  specs?: readonly CatalogueSpec[];
  /** A released Vorgang (`quote_approved`): the release goes with the change. */
  approved: boolean;
  /** The release that would go — `null` while it is not loaded; the question is asked all the same. */
  approval: { by: string | null; at: string | null } | null;
  /** What lies on the board; `null` where nothing does. */
  laid: LaidGratings | null;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * „Rosttyp ändern“ — the one question a change of type asks, in the planning
 * head and at the Eingang alike (Ruling G-2).
 *
 * First the release that goes (where there is one, R-4, in the words every
 * facade gesture asks in), then what happens to the gratings — „Die Roste
 * werden 288 statt 300 mm breit; die Kalkulation wird neu.“ (R-7, R-10) —
 * and then what a person set by hand and the change leaves standing (Ruling
 * G-1): hand-set brackets and typed blanks stay, and are to be checked. The
 * focus lands on „Abbrechen“.
 */
export function GratingTypeQuestion({ from, to, specs = [], approved, approval, laid, onCancel, onConfirm }: GratingTypeQuestionProps) {
  // Appended to the width sentence, one paragraph (Ruling G-1).
  const consequence =
    laid !== null
      ? [
          typeChangeSentence({
            fromWidthMm: from !== null ? gratingTypeWidthMm(from, specs) : null,
            toWidthMm: gratingTypeWidthMm(to, specs),
            following: laid.following,
            ownTyped: laid.ownTyped,
          }),
          handSetSentence(laid),
        ]
          .filter((part) => part !== "")
          .join(" ")
      : null;
  return (
    <Dialog
      title="Rosttyp ändern"
      subtitle={from !== null ? `${gratingTypeLabel(to)} statt ${gratingTypeLabel(from)}` : gratingTypeLabel(to)}
      onClose={onCancel}
    >
      {approved ? (
        <p className={styles.release}>{releaseLossSentence(approval?.by ?? null, approval?.at ?? null)}</p>
      ) : null}
      {consequence !== null ? <p className={styles.text}>{consequence}</p> : null}
      <DialogFooter>
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button variant="primary" onClick={onConfirm}>
          Rosttyp ändern
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
